import { useTranslation } from 'react-i18next';

import { CHANNEL_SHORT_NAME_MAP, TChannelProtocol } from '@/entities/channel';
import { Dialog, TDialogProps } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/lib/Button';

import { useBillingDialog } from '../model/useBillingDialog';

import { PaymentOrder } from './PaymentOrder';
import { SelectTariff } from './SelectTariff';

interface IBillingDialogProps extends TDialogProps {
  open: boolean;
  onClose: () => void;
  onBack: () => void;
  channelProtocol?: TChannelProtocol;
  isRenewing?: boolean;
  channelId?: string;
  renderIconWithLabel?: (
    renderLabelByProtocol: (protocol: TChannelProtocol) => string
  ) => React.ReactNode;
}

export const BillingDialog = ({
  open,
  onClose,
  onBack,
  channelProtocol,
  isRenewing,
  channelId,
  renderIconWithLabel,
  ...otherProps
}: IBillingDialogProps) => {
  const { t } = useTranslation();

  const {
    currentStep,
    selectedTariff,
    selectedPlan,
    selectedPaymentMethodId,
    isPartnerPayment,
    partnerDiscountRate,
    isPending,
    setSelectedTariff,
    setSelectedPlan,
    setSelectedPaymentMethodId,
    handleBack,
    handleContinue,
    handleClose,
    reservePaymentTabForCurrentMethod,
    getContinueButtonText,
  } = useBillingDialog({
    channelProtocol,
    isRenewing,
    channelId,
  });
  const isContinueDisabled =
    currentStep === 'selectTariff' && (!selectedTariff || !selectedPlan);

  return (
    <Dialog
      disableEscapeKeyDown={currentStep === 'payment'}
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' && currentStep === 'payment') {
          return;
        }
        handleClose(onClose);
      }}
      title={
        isRenewing
          ? t('billing:extendSubscription')
          : t('billing:addSubscriptionTitle')
      }
      additionalTitleSlot={renderIconWithLabel?.(
        (protocol: TChannelProtocol) => CHANNEL_SHORT_NAME_MAP[protocol]
      )}
      leftButtonSlot={
        <Button variant="outlined" onClick={() => handleBack(onBack)}>
          {t('common:buttons.back')}
        </Button>
      }
      rightButtonSlot={
        <Button
          variant="contained"
          onClickCapture={reservePaymentTabForCurrentMethod}
          onClick={() => handleContinue(onClose)}
          loading={isPending}
          disabled={isContinueDisabled}
        >
          {getContinueButtonText()}
        </Button>
      }
      noContentPadding={currentStep === 'payment'}
      {...otherProps}
    >
      {currentStep === 'selectTariff' ? (
        <SelectTariff
          channelProtocol={channelProtocol}
          selectedTariff={selectedTariff}
          selectedPlan={selectedPlan}
          onTariffChange={setSelectedTariff}
          onPeriodChange={setSelectedPlan}
          isPartnerPayment={isPartnerPayment}
          partnerDiscountRate={partnerDiscountRate}
        />
      ) : (
        <PaymentOrder
          channelProtocol={channelProtocol}
          selectedTariff={selectedTariff}
          selectedPlan={selectedPlan}
          selectedPaymentMethodId={selectedPaymentMethodId}
          onPaymentMethodChange={setSelectedPaymentMethodId}
          isPartnerPayment={isPartnerPayment}
          partnerDiscountRate={partnerDiscountRate}
        />
      )}
    </Dialog>
  );
};
